import * as React from 'react'

const MOBILE_BREAKPOINT = 768

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(
    window.innerWidth < MOBILE_BREAKPOINT
  )

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener('change', onChange)
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  return !!isMobile
}

export type MobileDevice = {
  isMobileDevice: boolean
  isTablet: boolean
  isPhone: boolean
  isIOS: boolean
  isAndroid: boolean
  isSafari: boolean
  isInApp: boolean
}

export function useMobileDevice(): MobileDevice {
  const [deviceInfo, setDeviceInfo] = React.useState<MobileDevice>({
    isMobileDevice: false,
    isTablet: false,
    isPhone: false,
    isIOS: false,
    isAndroid: false,
    isSafari: false,
    isInApp: false,
  })

  React.useEffect(() => {
    const userAgent = navigator.userAgent || (navigator as any).vendor || (window as any).opera

    // Detect iOS devices
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream

    // Detect Android devices
    const isAndroid = /android/i.test(userAgent)

    // Detect tablets (iPad or Android tablets)
    const isTablet =
      /iPad/.test(userAgent) ||
      (/android/i.test(userAgent) && !/mobile/i.test(userAgent)) ||
      // Additional tablet detection for Android tablets that might include "mobile"
      (/android/i.test(userAgent) && /tablet/i.test(userAgent))

    // Detect phones (iPhone, iPod, or Android phones)
    const isPhone =
      (/iPhone|iPod/.test(userAgent) && !(window as any).MSStream) ||
      (/android/i.test(userAgent) && /mobile/i.test(userAgent) && !/tablet/i.test(userAgent))

    // Detect Safari
    const isSafari = /Safari/.test(userAgent) && /Apple Computer/.test((navigator as any).vendor)

    // Detect if running in an app (standalone mode)
    const isInApp =
      (navigator as any).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches

    // Check if it's a mobile device (phone or tablet)
    const isMobileDevice = isIOS || isAndroid

    setDeviceInfo({
      isMobileDevice,
      isTablet,
      isPhone,
      isIOS,
      isAndroid,
      isSafari,
      isInApp,
    })
  }, [])

  return deviceInfo
}
